import { createSelector } from 'reselect';

import { currencyCode as geoCurrencyCode } from 'Helpers/Geo/Country';
import calculationSelector, {
  cartStateSelector,
  pickAndMixAllSelector,
  reportOrderCalcError,
} from 'Redux/reselects/order-calc/calculation-selector';
import { isActivePickAndMix } from 'Redux/reselects/order-calc/normalise-pick-and-mixes';

/**
 * What a cart line shows (Stage 7.3): the prices of one product, for the price
 * components, the cards, the modals and the upsell event. It replaces the
 * `item.out` of the old legacy cart adapter (deleted in Stage 7.4), with the
 * same fields, in minor units, read from the module lines:
 *
 *   - `price`: the "was" price (display only, see displayPrice).
 *   - `discountPrice`: what the unit costs before the voucher.
 *   - `isStarDeal`: only when the product is a star deal.
 *   - `couponApplies`, `couponPrice`, `totalSavingPercent`: only when the
 *     voucher applies to the line. `couponPrice` is the module paid amount, so
 *     it is the amount in the cart total (legacy `couponPriceIsPaid` is always
 *     true now, and is gone).
 *   - `pickAndMix`: only when the line is in a pick and mix tier (Stage 7.4):
 *     `slug`, `price` (the unit price), `quantity` (the tier quantity),
 *     `baseQuantity` (the quantity of the tier that applies), `promoPrice`
 *     (the sum of the unit prices in the tier) and, when the voucher applies,
 *     `appliedDiscount`. These are in major units, as the components use them.
 *
 * The view is of the first unit of the product (line `#0`), as the adapter did:
 * the price components multiply it by `item.quantity` (Stage 7 decision D4).
 * The cart totals come from all the lines and are exact.
 */

// Post-promo, pre-pick-and-mix, pre-voucher line price
const promoPrice = line => line.pricing.listPrice - line.pricing.promoDiscountAmount;

// Pre-voucher line price, on the same basis as the module totals
// (compute-totals.js). For JPY this includes the per-line rounding (D16).
// Otherwise it is the promo price, or the pick and mix share when the line is
// in a tier.
const preVoucherPrice = line => line.paid.total + (line.voucher?.discountAmount ?? 0);

// The "was" price. Legacy cartcalc.js:303-315. A non-SRP bundle shows what its
// games are worth: the context builder sends that as the reference price
// (order-calc 1.0.7, decision D9), and the module line has it, raised to what
// the line costs. It has none when the worth is the price (the module line
// then has none, and listPrice is the worth). Other products show their price.
const displayPrice = (item, line, currencyCode) => {
  if (item.mystery) return promoPrice(line);

  if (item.type === 'bundle' && !item.is_srp_bundle) {
    return line.pricing.referencePrice ?? line.pricing.listPrice;
  }

  return Math.floor(item.price[currencyCode]);
};

// Legacy cartcalc.js:501-504, from the paid total
const totalSavingPercent = (item, couponPrice, currencyCode) => {
  const actualPrice = (item.type === 'bundle' && !item.mystery)
    ? item.fullPrice[currencyCode]
    : item.price[currencyCode];

  return Math.round(((couponPrice - actualPrice) / actualPrice) * 100);
};

/**
 * @param   {Object}   item                The basket item (state.cart.items).
 * @param   {Object[]} lines               The module lines of the item, one for each unit.
 * @param   {Object}   options
 * @param   {boolean}  options.voucherApplied  `order.voucher.applied`.
 * @param   {string}   options.currencyCode    ISO 4217.
 * @returns {Object}                       The view. See the top of this file.
 */
export const lineView = (
  item,
  lines,
  { voucherApplied, currencyCode, pickAndMixById = new Map(), tierTotalByPromotionId = {} },
) => {
  const line = lines[0];
  const view = {
    price: displayPrice(item, line, currencyCode),
    discountPrice: preVoucherPrice(line),
  };

  if (item.current_discount?.star_deal) view.isStarDeal = true;

  if (voucherApplied && line.voucher?.included) {
    view.couponApplies = true;
    view.couponPrice = line.paid.total;
    view.totalSavingPercent = totalSavingPercent(item, line.paid.total, currencyCode);
  }

  const linePickAndMix = line.pricing.pickAndMix;
  const pickAndMix = linePickAndMix && pickAndMixById.get(linePickAndMix.promotionId);

  if (pickAndMix) {
    // The tier that the module used: the highest tier the quantity reaches
    const tier = [...(pickAndMix.tiers ?? [])]
      .sort((a, b) => b.quantity - a.quantity)
      .find(({ quantity }) => linePickAndMix.tierQuantity >= quantity);

    view.pickAndMix = {
      slug: pickAndMix.slug,
      price: linePickAndMix.unitPrice / 100,
      quantity: linePickAndMix.tierQuantity,
      baseQuantity: tier?.quantity,
      promoPrice: tierTotalByPromotionId[linePickAndMix.promotionId] / 100,
    };

    if (view.couponApplies) view.pickAndMix.appliedDiscount = line.paid.total / 100;
  }

  return view;
};

// What an item shows when the cart could not be calculated: zero prices, as
// the error cart. The cart shows the error message.
const ERROR_VIEW = Object.freeze({ price: 0, discountPrice: 0 });

/**
 * The view of every product in the cart.
 * @param   {Object}   calculation          { order, error, now }, from calculationSelector.
 * @param   {Object}   cart                 state.cart
 * @param   {string}   currencyCode         ISO 4217
 * @param   {Object[]} [pickAndMixAll=[]]   state.pickAndMix.all
 * @returns {Object}                        { [itemId]: view }. The error view for each product on an error.
 */
export const lineViews = (calculation, cart, currencyCode, pickAndMixAll = []) => {
  const items = (cart?.items ?? []).filter(Boolean);
  const { order } = calculation;

  if (!order) {
    return Object.fromEntries(items.map(item => [String(item._id), ERROR_VIEW]));
  }

  const linesByItemId = {};
  order.lines.forEach((line) => {
    linesByItemId[line.itemId] = linesByItemId[line.itemId] ?? [];
    linesByItemId[line.itemId].push(line);
  });
  const voucherApplied = !!order.voucher?.applied;
  const pickAndMixById = new Map((pickAndMixAll ?? [])
    .filter(pickAndMix => isActivePickAndMix(pickAndMix, calculation.now))
    .map(pickAndMix => [String(pickAndMix._id), pickAndMix]));
  const tierTotalByPromotionId = {};

  order.lines.forEach((line) => {
    const promotionId = line.pricing.pickAndMix?.promotionId;

    if (promotionId) {
      tierTotalByPromotionId[promotionId] = (tierTotalByPromotionId[promotionId] ?? 0)
        + line.pricing.pickAndMix.unitPrice;
    }
  });

  return Object.fromEntries(items
    .filter(item => linesByItemId[String(item._id)])
    .map(item => [
      String(item._id),
      lineView(item, linesByItemId[String(item._id)], {
        voucherApplied,
        currencyCode,
        pickAndMixById,
        tierTotalByPromotionId,
      }),
    ]));
};

// See cartDisplaySelector: a display failure shows zero prices and is reported.
export const lineViewsSelector = createSelector(
  calculationSelector,
  cartStateSelector,
  pickAndMixAllSelector,
  (calculation, cart, pickAndMixAll) => {
    try {
      return lineViews(calculation, cart, geoCurrencyCode, pickAndMixAll);
    } catch (error) {
      reportOrderCalcError(error, { source: 'line-view' });

      return lineViews({ order: null, error }, cart, geoCurrencyCode);
    }
  },
);

/**
 * The price (major units) of each cart entry that the GTM `cartAtCart` event
 * sends. Pure, so a spec can compare it with the legacy computation.
 * @param   {Object[]} items        The cart items, one entry for each unit.
 * @param   {Object}   views        { [itemId]: view }, from lineViews.
 * @param   {string}   currencyCode ISO 4217
 * @returns {Object[]}              [{ [itemId]: price }] in item order.
 */
export const upsellEventItems = (items, views, currencyCode) => items.map((item) => {
  const view = views[String(item._id)];
  let price = item.price?.[currencyCode] ?? 0;

  if (view) price = (view.discountPrice / 100) * item.quantity;

  if (item.type === 'bundle' && !item.is_srp_bundle) {
    price = ((item.price?.[currencyCode] ?? 0) / 100) * item.quantity;
  }

  if (view && view.couponPrice > 0) price = (view.couponPrice / 100) * item.quantity;
  if (view?.pickAndMix) price = view.pickAndMix.price * item.quantity;

  return { [item._id]: price };
});
